import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
} from 'recharts';
import { Paper, Alert, Chip, Group } from '@mantine/core';
import { useChartHeight } from '@/hooks/useChartHeight';
import { useIsMobile } from '@/hooks/useIsMobile';
import { calculateNiceAxisTicks } from '@/utils/formatters';
import { useTimeAxis } from '@/hooks/useTimeAxis';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ChartSkeleton } from '@/components/charts/ChartSkeleton';
import { ChartHeader } from '@/components/charts/ChartHeader';
import { CHART_GRID_STROKE, CHART_AXIS_TICK, CHART_LEGEND_STYLE } from '@/utils/chartStyles';
import { ANALYSIS_SERIES_COLORS, CHART_CROSSHAIR_COLOR } from '@/utils/chartColors';
import { ChartTooltipContent } from '@/components/charts/ChartTooltipContent';
import type { PowerDataPoint, PowerChannel } from '@/api/power';

interface OutletPowerChartProps {
  data: PowerDataPoint[];
  channels: PowerChannel[];
  isLoading?: boolean;
  error?: Error | null;
}

export function OutletPowerChart({
  data,
  channels,
  isLoading,
  error,
}: OutletPowerChartProps) {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartHeight = useChartHeight();
  const isMobile = useIsMobile();

  // Filter to watts channels only
  const wattsChannels = useMemo(
    () => channels.filter(c => c.reading_type === 'watts'),
    [channels],
  );

  // State for visible channel dids
  const [visibleDids, setVisibleDids] = useState<string[]>([]);

  // Sync visibleDids when channels load asynchronously
  useEffect(() => {
    if (wattsChannels.length > 0 && visibleDids.length === 0) {
      setVisibleDids(wattsChannels.map(c => c.did));
    }
  }, [wattsChannels, visibleDids.length]);

  // Convert string timestamps to numbers for the numeric X-axis
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return data.map(point => ({
      ...point,
      timestamp: new Date(point.timestamp).getTime(),
    }));
  }, [data]);

  // Derive date range from data for time axis
  const { startDate, endDate } = useMemo(() => {
    if (chartData.length === 0) {
      const now = new Date();
      return { startDate: now, endDate: now };
    }
    return {
      startDate: new Date(chartData[0]!.timestamp),
      endDate: new Date(chartData[chartData.length - 1]!.timestamp),
    };
  }, [chartData]);

  const timeAxis = useTimeAxis(startDate, endDate);

  // Calculate Y-axis from all visible channel values
  const yAxisConfig = useMemo(() => {
    if (!data || data.length === 0 || visibleDids.length === 0) {
      return calculateNiceAxisTicks(0, 100, 5, false);
    }

    const values: number[] = [];
    for (const point of data) {
      for (const did of visibleDids) {
        const v = point[did];
        if (v != null && typeof v === 'number' && !isNaN(v)) {
          values.push(v);
        }
      }
    }

    if (values.length === 0) {
      return calculateNiceAxisTicks(0, 100, 5, false);
    }

    // Reduce rather than spread: Math.min(...values) overflows the call stack
    // once a long date range produces hundreds of thousands of values.
    const min = values.reduce((a, b) => (b < a ? b : a));
    const max = values.reduce((a, b) => (b > a ? b : a));
    return calculateNiceAxisTicks(min, max, 5, false);
  }, [data, visibleDids]);

  // Map channel did to a stable color index
  const channelColorMap = useMemo(() => {
    const map = new Map<string, string>();
    wattsChannels.forEach((ch, idx) => {
      map.set(ch.did, ANALYSIS_SERIES_COLORS[idx % ANALYSIS_SERIES_COLORS.length]!);
    });
    return map;
  }, [wattsChannels]);

  // Early returns AFTER all hooks
  if (isLoading) {
    return <ChartSkeleton title="Per-Outlet Power" />;
  }

  if (error) {
    return (
      <Alert color="red" title="Error loading outlet power data">
        {error.message}
      </Alert>
    );
  }

  if (!data || data.length === 0 || wattsChannels.length === 0) {
    return (
      <Alert color="blue" title="No data available">
        No power data found for the selected date range.
      </Alert>
    );
  }

  return (
    <Paper ref={chartRef} p={{ base: 'sm', md: 'lg' }} withBorder radius={12} bg="var(--rdh-card-bg)">
      <ChartHeader
        title="Per-Outlet Power"
        subtitle={isMobile ? '(Watts)' : undefined}
        chartRef={chartRef}
      />

      {/* Channel toggle chips */}
      <Chip.Group multiple value={visibleDids} onChange={setVisibleDids}>
        <Group gap="xs" mb="sm" wrap="wrap">
          {wattsChannels.map((ch) => (
            <Chip key={ch.did} value={ch.did} size="xs" variant="outline">
              {ch.outlet_name ?? ch.did}
            </Chip>
          ))}
        </Group>
      </Chip.Group>

      <ResponsiveContainer width="100%" height={chartHeight}>
        <ComposedChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} vertical={false} />

          <XAxis
            dataKey="timestamp"
            type="number"
            domain={[startDate.getTime(), endDate.getTime()]}
            ticks={timeAxis.ticks}
            tickFormatter={timeAxis.formatTick}
            tick={CHART_AXIS_TICK}
            axisLine={false}
            tickLine={false}
            height={isMobile ? 35 : 50}
          />

          <YAxis
            domain={yAxisConfig.domain}
            ticks={yAxisConfig.ticks}
            tickFormatter={yAxisConfig.tickFormatter}
            width={isMobile ? 35 : 60}
            tick={CHART_AXIS_TICK}
            axisLine={false}
            tickLine={false}
          />

          <Tooltip
            cursor={{
              stroke: CHART_CROSSHAIR_COLOR,
              strokeWidth: 1,
              strokeDasharray: '4 4',
            }}
            content={
              <ChartTooltipContent
                valueFormatter={(v) => `${v.toFixed(1)}W`}
              />
            }
          />

          <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={CHART_LEGEND_STYLE} />

          {/* One Line per visible watts channel */}
          {wattsChannels
            .filter(ch => visibleDids.includes(ch.did))
            .map((ch) => (
              <Line
                key={ch.did}
                type="linear"
                dataKey={ch.did}
                name={ch.outlet_name ?? ch.did}
                stroke={channelColorMap.get(ch.did)}
                strokeWidth={1.5}
                dot={false}
                activeDot={{ r: 6 }}
                connectNulls={false}
                isAnimationActive={false}
              />
            ))}
        </ComposedChart>
      </ResponsiveContainer>
    </Paper>
  );
}
